<template>
  <!-- Komponent layoutu z dynamicznym sprawdzaniem uprawnień -->
  <component v-if="hasAccess($route.access)" :is="$route.meta.layout || 'div'">
    <!-- keep-alive tylko dla HomeView (Bieżący pomiar): po wyjściu z zakładki komponent
         NIE jest niszczony, więc interwał pollingu i bufor pojazdów (current.vehicles)
         żyją dalej w tle. Po powrocie widok pokazuje się natychmiast, bez ładowania od zera.
         Reszta widoków tworzona jest normalnie (świeży mount przy każdym wejściu). -->
    <router-view v-slot="{ Component }">
      <keep-alive :include="['HomeView']">
        <component :is="Component" />
      </keep-alive>
    </router-view>
  </component>

  <!-- Przycisk powrotu do góry strony -->
  <button
    @click="backToTop"
    :class="{ 'd-none': showTopButton }"
    type="button"
    class="btn btn-floating btn-lg"
    id="btn-back-to-top"
  >
    <i class="icon icon-arrow-up"></i>
  </button>

  <!-- Globalne powiadomienia (sukces/błąd operacji) — jeden komponent nadrzędny
       dla całej aplikacji. Montowany poza <router-view>, więc komunikat przeżywa
       zmianę trasy (np. potwierdzenie zmiany hasła widoczne już na /login). -->
  <AppNotifications />

  <!-- Globalny modal do obsługi np. ticketów -->
  <Ticket
    :title="modalState.title"
    :isVisible="modalState.isVisible"
    @close="hideModal"
  >
    <component v-if="modalState.content" :is="modalState.content" />
  </Ticket>
</template>

<script setup>
import { useUserStore } from "@/stores/user.store";
import Ticket from "@/components/TicketModal.vue";
import AppNotifications from "@/components/ui/AppNotifications.vue";
import { onMounted, reactive } from "vue";

// Uwaga: brak ręcznego push("/") dla niezalogowanych — router.beforeEach już to obsługuje
// (i poprawnie respektuje publiczne trasy /login, /reset-password*, /password-expired).

// Funkcja sprawdzająca, czy użytkownik ma dostęp do danego zasobu/widoku
function hasAccess(id) {
  const userStore = useUserStore();
  // Jeżeli access nie jest wymagany albo użytkownik ma odpowiednie uprawnienia – true
  return id === undefined || userStore.hasAccess(id);
}

// Zarządzanie stanem globalnego modala (np. do ticketów)
const modalState = reactive({
  isVisible: false,
  title: "",
  content: null, // Dynamiczny komponent jako treść modala
});

// Funkcja wyświetlająca modal (możesz wywołać z dowolnego miejsca przez window.$modal.showModal)
function showModal(title, component) {
  modalState.title = title;
  modalState.content = component;
  modalState.isVisible = true;
}

// Funkcja zamykająca modal
function hideModal() {
  modalState.isVisible = false;
  modalState.content = null;
}

// Udostępnienie globalnej obsługi modala przez window.$modal
window.$modal = { showModal, hideModal };

// Dane zgłaszającego (imię, nazwisko, e-mail) dociągamy w tle zaraz po starcie aplikacji,
// żeby modal zgłoszenia problemu otwierał się już wypełniony — bez oczekiwania na API po
// kliknięciu ikony „!”. Cache powstaje tylko dla użytkownika z uprawnieniem do zgłoszeń
// (sprawdzane w `refreshTicketReporter`), a dla niezalogowanego prefetch nie robi nic.
onMounted(() => {
  useUserStore().prefetchTicketReporter();
});
</script>

<script>
// Klasyczny script - tu obsługiwany jest przycisk przewijania do góry strony
export default {
  name: "CarViver",
  mounted() {
    // Gdy komponent zamontowany – ustawiamy nasłuchiwanie przewijania strony
    const that = this;
    window.onscroll = function () {
      that.scrollFunction();
    };
  },
  data() {
    return {
      showTopButton: true,    // Kontroluje widoczność przycisku "do góry"
      triggerPoint: 300,      // Po ilu pikselach scrolowania pojawia się przycisk
    };
  },
  methods: {
    // Funkcja wywoływana przy przewijaniu strony, ustala widoczność przycisku
    scrollFunction() {
      if (
        document.body.scrollTop > this.triggerPoint ||
        document.documentElement.scrollTop > this.triggerPoint
      ) {
        this.showTopButton = false;
      } else {
        this.showTopButton = true;
      }
    },
    // Przewijanie do góry strony
    backToTop() {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },
  },
};
</script>

<style>
/* Rodzina fontów i minimalna wysokość kolumny treści.
   Pozostałe reguły globalne (powierzchnie, typografia, kontrolki, tabele) są
   w `assets/css/design-system.css` — nie duplikujemy ich tutaj. */

body {
  margin: 0;
}

#app {
  font-family: Ubuntu, "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-style: normal;
}

.content-container > div,
.content-container .content-wrap > .container {
  min-height: 400px;
}
</style>
